// components/info-cards/DepotInfoCard.tsx
import { ConnectProto } from "@racoon/api";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { useMapLocations } from "~/shared/api/locations/use-map-locations";
import { useEffect, useMemo } from "react";
import { Row } from "./row";
import { locationTypeStyle } from "~/features/map-view/model/constants/location-types";
import { getEnumName } from "@racoon/lib";

type Props = {
  id: string;
  setHeader?: (element: React.ReactNode) => void;
};

export function DepotInfoCard({ id, setHeader }: Props) {
  const { data: { locations: depots = [] } = {}, isLoading } = useMapLocations();

  const dataWithLocalIndex = useMemo(() => {
    return depots?.map((location, index) => ({
      ...location,
      localIndex: index + 1,
    }));
  }, [depots]);

  const location = useMemo(() => {
    return dataWithLocalIndex?.find((p) => BigInt(p.id) === BigInt(id));
  }, [dataWithLocalIndex, id]);

  const t = useT();

  // Set header when data is loaded
  useEffect(() => {
    if (location && setHeader) {
      const { Icon } = locationTypeStyle(location.type);
      const element = (
        <span className="font-semibold text-blue-600 flex items-center gap-2">
          <Icon className="h-6 w-6 shrink-0" />
          <span>{location.name}</span>
        </span>
      );
      setHeader(element);
    }
  }, [location, setHeader]);

  if (!location) {
    return (
      <div className="p-4 text-sm text-text-secondary">
        {t(Namespace.Common, isLoading ? "loading" : "noData")}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 flex-1 space-y-6">
        {/* Block 1 */}
        <div className="space-y-2">
          <Row label={t(Namespace.Infopanel, "labels.type")}>
            {t(
              Namespace.Enum,
              getEnumName(ConnectProto.LocationTypeSchema, location.type) as TranslationKey,
            )}
          </Row>

          <Row label={t(Namespace.Infopanel, "labels.geolocation")}>
            {location.gisLink ? (
              <a
                href={location.gisLink}
                target="_blank"
                rel="noreferrer"
                className="!text-text-primary underline"
              >
                2ГИС ссылка
              </a>
            ) : (
              t(Namespace.Infopanel, "empty")
            )}
          </Row>
          <Row label={t(Namespace.Infopanel, "labels.address")}>
            {location.address ? <span>{location.address}</span> : t(Namespace.Infopanel, "empty")}
          </Row>
        </div>
      </div>
    </div>
  );
}
